import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportSelector = ({ selectedReport, onReportSelect, onFavoriteToggle, favoriteReports }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const reportCategories = [
    {
      id: 'card-usage',
      title: 'Card Usage Reports',
      icon: 'CreditCard',
      description: 'Track card activation, usage patterns, and performance metrics',
      reports: [
        { id: 'card-activity', name: 'Card Activity Summary', description: 'Daily/weekly/monthly card usage statistics' },
        { id: 'card-performance', name: 'Card Performance Analysis', description: 'Success rates and failure patterns' },
        { id: 'card-lifecycle', name: 'Card Lifecycle Report', description: 'From registration to deactivation tracking' },
        { id: 'card-geographic', name: 'Geographic Usage Distribution', description: 'Location-based usage patterns' }
      ]
    },
    {
      id: 'payment-analytics',
      title: 'Payment Analytics',
      icon: 'DollarSign',
      description: 'Financial transaction analysis and revenue insights',
      reports: [
        { id: 'payment-summary', name: 'Payment Summary', description: 'Total payments, success rates, and trends' },
        { id: 'revenue-analysis', name: 'Revenue Analysis', description: 'Monthly/quarterly revenue breakdown' },
        { id: 'payment-methods', name: 'Payment Methods Report', description: 'Analysis by payment type and provider' },
        { id: 'failed-payments', name: 'Failed Payments Analysis', description: 'Failed transaction patterns and reasons' }
      ]
    },
    {
      id: 'user-activity',
      title: 'User Activity Reports',
      icon: 'Users',
      description: 'User engagement and behavior analysis',
      reports: [
        { id: 'user-engagement', name: 'User Engagement Metrics', description: 'Login frequency and session duration' },
        { id: 'user-growth', name: 'User Growth Analysis', description: 'Registration trends and user acquisition' },
        { id: 'team-performance', name: 'Team Performance Report', description: 'Team vs individual user metrics' },
        { id: 'user-retention', name: 'User Retention Analysis', description: 'Churn rates and retention patterns' }
      ]
    },
    {
      id: 'billing-summaries',
      title: 'Billing Summaries',
      icon: 'Receipt',
      description: 'Comprehensive billing and invoice management reports',
      reports: [
        { id: 'billing-overview', name: 'Billing Overview', description: 'Monthly billing summary and outstanding amounts' },
        { id: 'invoice-analysis', name: 'Invoice Analysis', description: 'Invoice generation and payment tracking' },
        { id: 'subscription-metrics', name: 'Subscription Metrics', description: 'Plan usage and upgrade patterns' },
        { id: 'billing-forecasting', name: 'Billing Forecasting', description: 'Revenue predictions and growth projections' }
      ]
    }
  ];

  const filteredCategories = reportCategories.map(category => ({
    ...category,
    reports: category.reports.filter(report =>
      report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.reports.length > 0);

  const handleReportClick = (categoryId, reportId) => {
    onReportSelect({ categoryId, reportId });
  };

  const isReportFavorite = (categoryId, reportId) => {
    return favoriteReports.some(fav => fav.categoryId === categoryId && fav.reportId === reportId);
  };

  return (
    <div className="h-full flex flex-col bg-surface border-r border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-text-primary mb-3">Report Templates</h2>
        
        {/* Search */}
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          />
        </div>
      </div>

      {/* Report Categories */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {filteredCategories.map((category) => (
          <div key={category.id} className="space-y-2">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name={category.icon} size={18} className="text-accent" />
              <h3 className="font-medium text-text-primary">{category.title}</h3>
            </div>
            
            <div className="space-y-1">
              {category.reports.map((report) => (
                <div
                  key={report.id}
                  className={`group p-3 rounded-lg border cursor-pointer transition-all duration-150 ${
                    selectedReport?.categoryId === category.id && selectedReport?.reportId === report.id
                      ? 'bg-accent/10 border-accent text-accent' :'border-border hover:bg-hover hover:border-border-muted'
                  }`}
                  onClick={() => handleReportClick(category.id, report.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-text-primary group-hover:text-text-primary mb-1">
                        {report.name}
                      </h4>
                      <p className="text-xs text-text-muted line-clamp-2">
                        {report.description}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFavoriteToggle(category.id, report.id);
                      }}
                      className="p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                    >
                      <Icon
                        name={isReportFavorite(category.id, report.id) ? "Star" : "StarOff"}
                        size={14}
                        className={isReportFavorite(category.id, report.id) ? "text-warning" : "text-text-muted"}
                      />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {filteredCategories.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Search" size={32} className="text-text-muted mx-auto mb-3" />
            <p className="text-text-muted">No reports found matching "{searchQuery}"</p>
          </div>
        )}
      </div>

      {/* Favorites Section */}
      {favoriteReports.length > 0 && (
        <div className="p-4 border-t border-border">
          <h3 className="text-sm font-medium text-text-primary mb-2 flex items-center">
            <Icon name="Star" size={16} className="text-warning mr-2" />
            Favorite Reports ({favoriteReports.length})
          </h3>
          <div className="space-y-1">
            {favoriteReports.slice(0, 3).map((fav) => {
              const category = reportCategories.find(cat => cat.id === fav.categoryId);
              const report = category?.reports.find(rep => rep.id === fav.reportId);
              
              if (!report) return null;
              
              return (
                <button
                  key={`${fav.categoryId}-${fav.reportId}`}
                  onClick={() => handleReportClick(fav.categoryId, fav.reportId)}
                  className="w-full text-left p-2 rounded text-sm text-text-muted hover:text-text-primary hover:bg-hover transition-colors duration-150"
                >
                  {report.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportSelector;